import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Users, Plus, Briefcase, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import RowActions from "../../components/RowActions";
import FileAttach, { AttachmentList } from "../../components/FileAttach";

import { useAuth } from "../../contexts/AuthContext";
import { localName, localFullName } from "../../lib/localName";

// Note: departments + positions are now loaded dynamically from /api/departments — each school manages its own.

const EMPTY = {
  full_name: "", full_name_ar: "", national_id: "", nationality: "SA", gender: "male",
  date_of_birth: "", phone: "", email: "", address: "",
  department: "", position: "", employment_type: "full_time",
  hired_at: "", base_salary: 0, bank_iban: "", status: "active", user_id: "",
  employee_no: "", notes: "",
  school_id: "",  // super_admin picks; school_admin auto-filled
  // New: optional account creation on employee creation
  create_account: false,
  account_role: "teacher",
  account_password: "",
};

const ACCOUNT_ROLES = ["school_admin", "academic", "finance", "teacher", "driver", "hr"];

export default function EmployeesPage() {
  const {  t , lang } = useI18n();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [schools, setSchools] = useState([]);
  const [filterDept, setFilterDept] = useState("all");
  const [filterSchool, setFilterSchool] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/employees").then(r => setItems(r.data || []));
  useEffect(() => {
    load();
    api.get("/users").then(r => setUsers(r.data || [])).catch(() => {});
    api.get("/schools").then(r => {
      const list = r.data || [];
      setSchools(list);
      // Default school_id for the create form: super_admin picks later; others use their first school
      if (!isSuperAdmin && list.length > 0) {
        setForm(f => ({ ...f, school_id: list[0].id }));
      }
    }).catch(() => {});
  }, [isSuperAdmin]);

  // Reload departments whenever the chosen school changes
  useEffect(() => {
    const sid = form.school_id || (schools[0]?.id);
    if (sid) {
      api.get(`/departments?school_id=${sid}`).then(r => setDepartments(r.data || [])).catch(() => {});
    }
  }, [form.school_id, schools.length]);

  // Selected department's positions (or fallback to combined positions across all departments for current employee's dept)
  const currentDept = departments.find(d => d.id === form.department || d.code?.toLowerCase() === form.department || d.name_ar === form.department);
  const positionsForDept = currentDept?.positions || [];

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ ...EMPTY, ...e }); setOpen(true); };
  const save = async () => {
    try {
      // Resolve school_id: super_admin must pick; others default to their assigned school
      const targetSchoolId = form.school_id || schools[0]?.id;
      if (!targetSchoolId) { toast.error(t("hep_no_schools")); return; }
      let user_id = form.user_id || null;

      // 🔑 If admin chose to create a fresh login account for this employee
      if (!editing && form.create_account) {
        if (!form.email) { toast.error(t("hep_email_required")); return; }
        if (!form.account_password || form.account_password.length < 6) {
          toast.error(t("hep_pw_too_short")); return;
        }
        try {
          const res = await api.post("/auth/register", {
            email: form.email,
            password: form.account_password,
            name: localFullName(form, lang) || form.email,
            role: form.account_role,
            school_id: targetSchoolId,
            phone: form.phone || null,
          });
          user_id = res.data.id;
          toast.success(`${t("hep_account_created")}: ${form.email}`);
        } catch (err) {
          toast.error(err.response?.data?.detail || t("hep_create_failed"));
          return;
        }
      }

      const payload = {
        ...form, school_id: targetSchoolId,
        base_salary: Number(form.base_salary) || 0,
        user_id: user_id,
      };
      // Strip account-only fields before sending to /employees
      delete payload.create_account;
      delete payload.account_role;
      delete payload.account_password;

      if (editing) await api.put(`/employees/${editing.id}`, payload);
      let provShown = false;
      if (!editing) {
        const resp = await api.post("/employees", payload);
        // Auto-provisioning (teacher/driver): backend emails a one-time "set your
        // password" link — no password is ever displayed here.
        const prov = resp.data?.auto_provisioned;
        if (prov && (prov.user_id || prov.driver_id)) {
          const roleLabel = prov.driver_id ? "السائق" : "المعلم";
          let msg;
          if (prov.action === "record_only") {
            msg = `تم إنشاء سجل ${roleLabel} وظهوره في وحدة النقل. لم يُنشأ حساب دخول لعدم وجود بريد إلكتروني.`;
          } else if (prov.reset_email_sent) {
            msg = prov.action === "created"
              ? `✉️ تم إنشاء حساب ${roleLabel} وإرسال رابط تعيين كلمة المرور إلى ${prov.email}`
              : `✉️ تم ربط حساب موجود وإرسال رابط تعيين كلمة المرور إلى ${prov.email}`;
          } else {
            msg = `تم إنشاء حساب ${roleLabel} (${prov.email}) — تعذّر إرسال البريد، تحقق من إعدادات البريد الإلكتروني`;
          }
          toast.success(msg, { duration: 12000 });
          provShown = true;
        }
      }
      if (!provShown) toast.success(t("success"));
      setOpen(false);
      load();
      // Refresh users list so the new account appears in the "link to user" picker
      api.get("/users").then(r => setUsers(r.data || [])).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (e) => {
    try { await api.delete(`/employees/${e.id}`); toast.success(t("success")); load(); }
    catch (er) { toast.error(er.response?.data?.detail || "Error"); }
  };

  const filtered = items.filter(i => {
    if (filterSchool !== "all" && i.school_id !== filterSchool) return false;
    if (filterDept !== "all" && i.department !== filterDept) return false;
    return true;
  });

  // Helpers to render the Arabic label of a department/position (by stored ID or legacy code)
  const deptLabel = (deptIdOrCode) => {
    if (!deptIdOrCode) return "—";
    const d = departments.find(x => x.id === deptIdOrCode || x.code?.toLowerCase() === deptIdOrCode.toLowerCase() || x.name === deptIdOrCode);
    return d?.name_ar || t(deptIdOrCode) || deptIdOrCode;
  };
  const positionLabel = (deptIdOrCode, posKey) => {
    if (!posKey) return "—";
    const d = departments.find(x => x.id === deptIdOrCode || x.code?.toLowerCase() === deptIdOrCode?.toLowerCase());
    const p = d?.positions?.find(x => x.key === posKey);
    return p?.label_ar || t(posKey) || posKey;
  };
  const schoolLabel = (sid) => schools.find(s => s.id === sid)?.name_ar || schools.find(s => s.id === sid)?.name || sid;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-3"><Users className="w-7 h-7 text-primary" /> {t("employees")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("total_employees")}: {items.length}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isSuperAdmin ? (
            <Select value={filterSchool} onValueChange={setFilterSchool}>
              <SelectTrigger className="w-56" data-testid="emp-filter-school"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("hep_all_schools")}</SelectItem>
                {schools.map(s => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Select value={user?.school_id || ""} disabled>
              <SelectTrigger className="w-56" data-testid="emp-filter-school"><SelectValue placeholder={t("select_school")} /></SelectTrigger>
              <SelectContent>
                {schools.map(s => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={filterDept} onValueChange={setFilterDept}>
            <SelectTrigger className="w-48" data-testid="emp-filter-dept"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("department")} — {t("all") || "all"}</SelectItem>
              {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name_ar}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openNew} data-testid="new-employee-btn"><Plus className="w-4 h-4 me-2" />{t("new_employee")}</Button></DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? t("edit") : t("new_employee")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                {isSuperAdmin && (
                  <div className="col-span-2">
                    <Label>{t("school")} <span className="text-destructive">*</span></Label>
                    <Select value={form.school_id || ""} onValueChange={v => setForm({ ...form, school_id: v, department: "", position: "" })}>
                      <SelectTrigger data-testid="emp-school"><SelectValue placeholder={t("hep_pick_school")} /></SelectTrigger>
                      <SelectContent>
                        {schools.map(s => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label>{t("nameAr")}</Label><Input value={form.full_name_ar} onChange={e => setForm({ ...form, full_name_ar: e.target.value })} data-testid="emp-name-ar" /></div>
                <div><Label>{t("nameEn")}</Label><Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} /></div>
                <div><Label>{t("national_id") || "National ID"}</Label><Input value={form.national_id} onChange={e => setForm({ ...form, national_id: e.target.value })} /></div>
                <div><Label>{t("employee_no")}</Label><Input value={form.employee_no} onChange={e => setForm({ ...form, employee_no: e.target.value })} /></div>
                <div><Label>{t("phone")}</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>{t("email")}</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>{t("department")}</Label>
                  <Select value={form.department} onValueChange={v => setForm({ ...form, department: v, position: "" })}>
                    <SelectTrigger data-testid="emp-dept"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {departments.length === 0 && <SelectItem value="__none" disabled>{t("hep_no_departments")}</SelectItem>}
                      {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name_ar}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{t("position")}</Label>
                  <Select value={form.position} onValueChange={v => setForm({ ...form, position: v })} disabled={positionsForDept.length === 0}>
                    <SelectTrigger data-testid="emp-position"><SelectValue placeholder={positionsForDept.length ? t("hep_pick_position") : t("hep_pick_dept_first")} /></SelectTrigger>
                    <SelectContent>
                      {positionsForDept.map(p => <SelectItem key={p.key} value={p.key}>{p.label_ar}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{t("employment_type")}</Label>
                  <Select value={form.employment_type} onValueChange={v => setForm({ ...form, employment_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">{t("full_time")}</SelectItem>
                      <SelectItem value="part_time">{t("part_time")}</SelectItem>
                      <SelectItem value="contract">{t("contract")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{t("hired_at") || "Hired At"}</Label><Input type="date" value={form.hired_at} onChange={e => setForm({ ...form, hired_at: e.target.value })} /></div>
                <div><Label>{t("base_salary")} ({t("hep_sar")})</Label><Input type="number" value={form.base_salary} onChange={e => setForm({ ...form, base_salary: e.target.value })} data-testid="emp-salary" /></div>
                <div><Label>{t("hep_iban")}</Label><Input value={form.bank_iban} onChange={e => setForm({ ...form, bank_iban: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>{t("user_accounts")} ({t("optional") || "optional"})</Label>
                  <Select value={form.user_id || "none"} onValueChange={v => setForm({ ...form, user_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {users.map(u => <SelectItem key={u.id} value={u.id}>{u.email} ({u.role})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">{t("hep_link_existing_help")}</p>
                </div>

                {/* 🔑 Inline account creation (only on new employee) */}
                {!editing && (
                  <div className="col-span-2 border border-primary/30 bg-primary/5 rounded-lg p-3 space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!form.create_account}
                        onChange={(e) => setForm({ ...form, create_account: e.target.checked })}
                        data-testid="emp-create-account"
                        className="w-4 h-4 accent-primary"
                      />
                      <span className="font-medium text-sm">{t("hep_create_login")}</span>
                    </label>
                    {form.create_account && (
                      <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-primary/20">
                        <div>
                          <Label>{t("hep_pw_required")} <span className="text-destructive">*</span></Label>
                          <Input
                            type="password"
                            value={form.account_password}
                            onChange={(e) => setForm({ ...form, account_password: e.target.value })}
                            placeholder={t("hep_pw_placeholder")}
                            data-testid="emp-account-password"
                            autoComplete="new-password"
                          />
                        </div>
                        <div>
                          <Label>{t("hep_login_role")}</Label>
                          <Select value={form.account_role} onValueChange={(v) => setForm({ ...form, account_role: v })}>
                            <SelectTrigger data-testid="emp-account-role"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ACCOUNT_ROLES.map(r => <SelectItem key={r} value={r}>{t(r) || r}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-xs text-muted-foreground sm:col-span-2">
                          {t("hep_login_info_a")} ({form.email || "—"}) {t("hep_login_info_b")}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                <div className="col-span-2">
                  <Label>{t("photo_id") || "Photo / ID document"}</Label>
                  <FileAttach
                    value={form.photo_id ? [{ id: form.photo_id, name: t("photo") || "photo" }] : []}
                    onChange={(list) => setForm({ ...form, photo_id: list[list.length - 1]?.id || null })}
                    testIdPrefix="emp-photo"
                  />
                </div>
              </div>
              <Button onClick={save} className="w-full mt-3" data-testid="emp-save">{t("save")}</Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border shadow-none">
        <ul className="divide-y divide-border">
          {filtered.map(e => (
            <li key={e.id} className="p-4 flex items-center gap-3 hover:bg-muted/30 smooth" data-testid={`emp-${e.id}`}>
              {e.photo_id ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${e.photo_id}`} alt="" className="w-10 h-10 rounded-lg object-cover bg-muted" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center"><Briefcase className="w-5 h-5" /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium">{localFullName(e, lang)}</div>
                <div className="text-xs text-muted-foreground">
                  {positionLabel(e.department, e.position)} · {deptLabel(e.department)} · {e.base_salary} {t("hep_sar")}
                  {isSuperAdmin && <span className="ms-2 text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">{schoolLabel(e.school_id)}</span>}
                </div>
              </div>
              <Link to={`/hr/employees/${e.id}`} className="text-xs text-primary underline" data-testid={`emp-view-${e.id}`}>{t("view") || "View"} <ChevronLeft className="w-3 h-3 inline lucide-rotate-rtl" /></Link>
              <RowActions item={e} onEdit={openEdit} onDelete={remove} testIdPrefix="emp" />
            </li>
          ))}
          {filtered.length === 0 && <li className="p-12 text-center text-muted-foreground">{t("no_data")}</li>}
        </ul>
      </Card>
    </div>
  );
}
